import { useState } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { breakpoints } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { Button, Heading, Text, ModalControlTriggerProps } from '@frontend/design-system';

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    background: radial-gradient(
      126.66% 192.95% at 41.6% 62.99%,
      ${theme.colors.success5} 0%,
      ${theme.colors.success30} 100%
    );
    border-radius: ${theme.borderRadius.medium} 0 0 0;
    align-items: center;
    gap: ${theme.spacing(2)};
    padding: ${theme.spacing(3)};
  `,
  icon: css`
    display: flex;
    justify-content: center;
    align-items: center;
    background: ${theme.colors.success5};
    border-radius: ${theme.borderRadius.full};
    width: ${theme.spacing(3)};
    height: ${theme.spacing(3)};
    margin-right: ${theme.spacing(1)};
  `,
  iconRow: css`
    display: flex;
    flex-direction: row;
    color: ${theme.colors.neutral70};
  `,
  heading: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    .subheader {
      width: 100%;
    }
    @media (min-width: ${breakpoints.medium.min}px) {
      .subheader {
        width: 70%;
      }
    }
  `,
  cardContainer: css`
    background: ${theme.colors.white};
    border-radius: ${theme.borderRadius.medium};
    display: flex;
    flex-direction: column;
    padding: ${theme.spacing(3)};
  `,
  list: css`
    display: flex;
    flex-direction: column;
    justify-content: space-evenly;
    margin-bottom: ${theme.spacing(3.5)};
    margin-top: ${theme.spacing(1.5)};
    @media (min-width: ${breakpoints.medium.min}px) {
      flex-direction: row;
      align-items: flex-start;
    }
  `,
  listItem: css`
    display: flex;
    align-items: center;
    padding: ${theme.spacing(1)};
    flex-direction: row;
    width: max-content;
    gap: ${theme.spacing(1)};
    @media (min-width: ${breakpoints.medium.min}px) {
      flex-direction: column;
      gap: none;
    }
  `,
  buttonRow: css`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${theme.spacing(2)};
  `,
};

interface SignupPageHeaderProps {
  isPaymentsBillingManager: boolean;
  stripeId?: string;
  modalTriggerProps: ModalControlTriggerProps;
  onOnboardClick: ({ stripeId, setLoading }: { stripeId: string; setLoading: (loading: boolean) => void }) => void;
}

export const SignupPageHeader = ({
  isPaymentsBillingManager,
  stripeId,
  modalTriggerProps,
  onOnboardClick,
}: SignupPageHeaderProps) => {
  const { t } = useTranslation('payments');
  const [loading, setLoading] = useState(false);

  const onSecondaryClick = () => {
    window.open('https://www.getweave.com/demo/campaign/xp/payments/', '_blank');
  };

  return (
    <div css={styles.container} data-testid='signup-page-header'>
      <span css={styles.iconRow}>
        <span css={styles.icon}>
          <Icon name='bulb-small' color='success' />
        </span>
        <Text weight='bold'>{t('Your plan includes Payments')}</Text>
      </span>
      <span css={styles.heading}>
        <Heading textAlign='center'>{t('Start Accepting Payments in Minutes')}</Heading>
        <Text size='large' textAlign='center' className='subheader'>
          {t("You already have access to our payment processing. Let's get you set up and collecting revenue today.")}
        </Text>
      </span>
      <div css={styles.cardContainer}>
        <Text weight='bold'>{t('All you need to start:')}</Text>
        <div css={styles.list}>
          <span css={styles.listItem}>
            <Icon name='credit-card' />
            <Text>{t('Owner SSN')}</Text>
          </span>
          <span css={styles.listItem}>
            <Icon name='fax-number' />
            <Text>{t('Employer Identification Number')}</Text>
          </span>
          <span css={styles.listItem}>
            <Icon name='building' />
            <Text>{t('Banking Information')}</Text>
          </span>
        </div>
        <span css={styles.buttonRow}>
          <Button variant='secondary' onClick={onSecondaryClick} trackingId='stripe-signup-talk-to-specialist-top'>
            {t('Talk to a Specialist')}
          </Button>
          <Button
            variant='primary'
            disabled={!isPaymentsBillingManager}
            loading={loading}
            showLabelWhenDisabled
            trackingId='stripe-signup-activate-now-top'
            hoverLabel={
              !isPaymentsBillingManager ? t('Must be a Payments Billing Manager to setup Stripe account') : undefined
            }
            {...(!stripeId ? modalTriggerProps : { onClick: () => onOnboardClick({ stripeId, setLoading }) })}
          >
            {!stripeId ? t('Activate Now') : t('Continue Onboarding')}
          </Button>
        </span>
      </div>
    </div>
  );
};
